// The shared table-sort logic, ported ONCE from js/table-sort.js as pure functions
// (the hook useSortableRows wraps these with React state + the FLIP animation). Ben's
// rule: table-sort is shared by League Table, Squad and Rankings — port it once, not
// per module. Match Results has no header row, so it isn't sortable (initSortableTable
// no-ops there), and it's left as a plain list.
//
// Faithful to table-sort.js: numeric-vs-text is auto-detected from the column's real
// data (row 0's value), not a header-name allowlist; name-like columns (Team/Player/
// Name) sort by surname (last word); GA/xGA/L default to ascending (lowest = best);
// everything else defaults descending for numbers, ascending for text.

/** A sortable column: its header label (drives name-like + low-first detection) and a
 *  getter for the value shown in that cell. `sortable: false` = the legacy `.no-sort`
 *  opt-out (e.g. League Table's Team column). */
export interface SortColumn<T> {
  label: string;
  get: (row: T) => string | number | null | undefined;
  sortable?: boolean;
}

export interface SortState {
  colIndex: number;
  asc: boolean;
}

// Columns where lowest = best, so the first click sorts ascending (table-sort.js:71).
// Matched against the lower-cased header label, exactly like the legacy.
export const SORT_LOW_FIRST = new Set(["ga", "xga", "l"]);

export function isColumnSortable<T>(col: SortColumn<T> | undefined): boolean {
  return !!col && col.sortable !== false;
}

/** Team / Player / Name columns — sorted by surname (table-sort.js:93). */
export function isNameLikeLabel(label: string): boolean {
  const t = label.trim().toLowerCase();
  return t === "team" || t.startsWith("team ") || t === "player" || t === "name";
}

function toStr(v: string | number | null | undefined): string {
  return v == null ? "" : String(v).trim();
}

/**
 * Is this column text (vs numeric)? Name-like columns are always text; otherwise it's
 * read from row 0's real value — a non-numeric sample (e.g. "England", "Full Back", or
 * an em-dash) means text. Mirrors table-sort.js:116 including the `!!sample` guard (an
 * empty first cell reads as text).
 */
export function isTextColumn<T>(col: SortColumn<T>, rows: readonly T[]): boolean {
  if (isNameLikeLabel(col.label)) return true;
  const sample = rows.length ? toStr(col.get(rows[0] as T)) : "";
  const isNumeric = !!sample && !Number.isNaN(Number(sample.replace(/[,%]/g, "")));
  return !isNumeric;
}

/**
 * The ascending flag for clicking a column: toggle if it's already the active column,
 * else the column's default (text or low-first → ascending; other numerics → descending).
 * table-sort.js:118.
 */
export function resolveAsc<T>(
  columns: readonly SortColumn<T>[],
  rows: readonly T[],
  colIndex: number,
  current: SortState | null,
): boolean {
  const col = columns[colIndex];
  if (!col) return true;
  const label = col.label.trim().toLowerCase();
  const isText = isTextColumn(col, rows);
  const defaultAsc = isText || SORT_LOW_FIRST.has(label);
  return current && current.colIndex === colIndex ? !current.asc : defaultAsc;
}

/**
 * Sort a copy of rows by one column. A name-like text column sorts on the surname (last
 * word); other text on the whole value; numerics via parseFloat (non-numeric → 0, so an
 * em-dash sinks like the legacy). Returns the rows unchanged for an absent/unsortable
 * column. Pure — never mutates the input (table-sort.js:123).
 */
export function sortByColumn<T>(
  rows: readonly T[],
  columns: readonly SortColumn<T>[],
  colIndex: number,
  asc: boolean,
): T[] {
  const col = columns[colIndex];
  if (!isColumnSortable(col) || !col) return rows.slice();
  const isNameLike = isNameLikeLabel(col.label);
  const isText = isTextColumn(col, rows);
  const copy = rows.slice();
  copy.sort((a, b) => {
    const va = toStr(col.get(a));
    const vb = toStr(col.get(b));
    if (isText) {
      const key = (s: string) => (isNameLike ? (s.split(" ").pop() ?? s) : s);
      return asc ? key(va).localeCompare(key(vb)) : key(vb).localeCompare(key(va));
    }
    const na = parseFloat(va) || 0;
    const nb = parseFloat(vb) || 0;
    return asc ? na - nb : nb - na;
  });
  return copy;
}
